import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import "./PdfViewer.scss";
import { Document, Page } from "react-pdf";
import CircularProgress from "@mui/material/CircularProgress";
import Draggable from "react-draggable";

export function FloatingPdfFileViewer({ fileUrl = null, onClose }) {
  const [portalContainer, setPortalContainer] = useState(null);
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!fileUrl) return;

    previousFocusRef.current = document.activeElement;
    const container = document.createElement("div");
    container.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: fit-content;
      height: fit-content;
      z-index: 9999;
      pointer-events: none;
    `;

    document.body.appendChild(container);
    setPortalContainer(container);

    return () => {
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
      setPortalContainer(null);
      previousFocusRef.current?.focus?.();
    };
  }, [fileUrl]);

  useEffect(() => {
    if (!portalContainer) return undefined;
    const focusTimer = window.setTimeout(() => {
      dialogRef.current?.querySelector(".pdf-viewer-close-button")?.focus();
    }, 0);
    return () => window.clearTimeout(focusTimer);
  }, [portalContainer]);

  const handleDialogKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose?.();
      return;
    }
    if (event.key !== "Tab") return;
    const focusableElements = Array.from(
      dialogRef.current?.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? []
    );
    if (focusableElements.length === 0) return;
    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!fileUrl || !portalContainer) return null;

  return ReactDOM.createPortal(
    <Draggable handle=".drag-handle">
      <div
        ref={dialogRef}
        className="pdf-viewer-draggable"
        style={{ pointerEvents: "auto" }}
        role="dialog"
        aria-modal="true"
        aria-label="Document viewer"
        onKeyDown={handleDialogKeyDown}
      >
        <div className="drag-handle pdf-viewer-header p-2">
          <span className="">Click to Drag</span>
          <button type="button" onClick={onClose} className="pdf-viewer-close-button" aria-label="Close document viewer">
            <span aria-hidden="true">X</span>
          </button>
        </div>
        <div>
          <PdfFileViewer fileUrl={fileUrl} onClose={onClose} />
        </div>
      </div>
    </Draggable>,
    portalContainer
  );
}

export default function PdfFileViewer({ fileUrl }) {
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [numPages, setNumPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const loadedPdfUrl = useRef(null);

  const HandleRetrievePdf = useCallback(async () => {
    if (!fileUrl || loadedPdfUrl.current === fileUrl) return;

    try {
      loadedPdfUrl.current = fileUrl;
      setSelectedPdf(fileUrl);
    } catch (error) {
      console.error("Error calling getFileUrl:", error);
    }
  }, [fileUrl]);

  useEffect(() => {
    if (fileUrl) {
      HandleRetrievePdf();
    }

    // Cleanup function to reset loaded PDF when component unmounts or contentId changes
    return () => {
      if (fileUrl) {
        loadedPdfUrl.current = null;
      }
    };
  }, [fileUrl, HandleRetrievePdf]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const onDocumentLoadError = (error) => {
    console.error("App.js: PDF Document load error:", error.message);
  };

  return (
    <div className="pdf-viewer-container">
      <div className="pdf-button-container">
        <button
          onClick={() =>
            setPageNumber(pageNumber === 1 ? numPages : pageNumber - 1)
          }
          className="pdf-button"
        >
          Previous
        </button>
        <div>
          {pageNumber}/{numPages}
        </div>
        <button
          onClick={() =>
            setPageNumber(pageNumber === numPages ? 1 : pageNumber + 1)
          }
          className="pdf-button"
        >
          Next
        </button>
      </div>
      <Document
        file={selectedPdf}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        loading={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              padding: "40px 20px",
            }}
          >
            <CircularProgress />
            <p style={{ marginTop: "10px" }}>Loading PDF...</p>
          </div>
        }
        style={{
          width: "100%",
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Page
          pageNumber={pageNumber}
          loading={
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
              <p style={{ marginTop: "10px" }}>Loading page {pageNumber}...</p>
            </div>
          }
          style={{
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            backgroundColor: "white",
            maxWidth: "100%",
            height: "auto",
            overflow: "auto",
            margin: "0 auto",
          }}
          renderTextLayer={true}
        />
      </Document>
    </div>
  );
}
